import type { ReactElement } from 'react';
import { formatWon } from '@/shared/lib';
import { Button, Input } from '@/shared/ui';
import type { DraftLineError, DraftLineForm } from '../model/draft-form';

export interface QuoteLineRowProps {
  readonly line: DraftLineForm;
  /** 0부터 — 화면 번호는 index + 1. */
  readonly index: number;
  readonly count: number;
  /** 표시용 로컬 계산 금액. 아직 정수가 아니면 null. */
  readonly amount: number | null;
  readonly error?: DraftLineError;
  readonly disabled?: boolean;
  readonly onChange: (patch: Partial<Omit<DraftLineForm, 'key'>>) => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
  readonly onRemove: () => void;
}

const CELL_CLASS = 'px-2 py-2 align-top';

/** 초안 품목 줄(견적 설계 §6-3) — 이름 1~100자, 수량 1 이상 정수, 단가 정수(할인은 음수). */
export const QuoteLineRow = ({
  line,
  index,
  count,
  amount,
  error,
  disabled = false,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: QuoteLineRowProps): ReactElement => {
  const number = index + 1;

  return (
    <tr className="border-t">
      <td className={CELL_CLASS}>
        <Input
          aria-label={`${number}번 줄 품목`}
          value={line.name}
          maxLength={100}
          hasError={Boolean(error?.name)}
          disabled={disabled}
          onChange={(event) => onChange({ name: event.target.value })}
        />
        {error?.name ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {error.name}
          </p>
        ) : null}
      </td>
      <td className={CELL_CLASS}>
        <Input
          aria-label={`${number}번 줄 수량`}
          inputMode="numeric"
          value={line.quantity}
          hasError={Boolean(error?.quantity)}
          disabled={disabled}
          className="text-right"
          onChange={(event) => onChange({ quantity: event.target.value })}
        />
        {error?.quantity ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {error.quantity}
          </p>
        ) : null}
      </td>
      <td className={CELL_CLASS}>
        <Input
          aria-label={`${number}번 줄 단가`}
          inputMode="numeric"
          value={line.unitPrice}
          hasError={Boolean(error?.unitPrice)}
          disabled={disabled}
          className="text-right"
          onChange={(event) => onChange({ unitPrice: event.target.value })}
        />
        {error?.unitPrice ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {error.unitPrice}
          </p>
        ) : null}
      </td>
      <td className={`${CELL_CLASS} text-right tabular-nums`}>
        {amount === null ? '-' : formatWon(amount)}
      </td>
      <td className={`${CELL_CLASS} whitespace-nowrap`}>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${number}번 줄 위로`}
            disabled={disabled || index === 0}
            onClick={onMoveUp}
          >
            ↑
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${number}번 줄 아래로`}
            disabled={disabled || index >= count - 1}
            onClick={onMoveDown}
          >
            ↓
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${number}번 줄 삭제`}
            disabled={disabled}
            onClick={onRemove}
          >
            삭제
          </Button>
        </div>
      </td>
    </tr>
  );
};
